/** biome-ignore-all lint/style/useNamingConvention: allow snake case */
import capitalize from 'lodash/capitalize';
import nth from 'lodash/nth';
import {useEffect, useMemo, useState} from 'react';
import {shallowEqual, useSelector, useStore} from 'react-redux';
import {saveEventThunk} from '~/CreateFabricInvitation/api/event';
import {UPDATE_GIFT_OPTIONS} from '~/common/fabric/reducers/giftingOptions/constants';
import {
  selectConfirmDeleteLastRegistry,
  selectRegistryUrls,
} from '~/common/fabric/reducers/giftingOptions/selectors';
import {eviteVendors} from '~/common/vendors/vendors';
import {Button} from '~library/atoms/Button';
import {Divider} from '~library/atoms/Divider';
import {Input} from '~library/atoms/Input';
import {Typography} from '~library/atoms/Typography';
import {HeartFilledIcon} from '~library/svg/HeartFilledIcon';
import {ThreeQuarterFilledEllipseIcon} from '~library/svg/ThreeQuarterFilledEllipseIcon';
import {tanzanite} from '~libSass/base/_exports.sass';
import {formatUrlWithProtocol, validURL} from './utils';

export const Registries = (props) => {
  const {nextButtonClicked, onRegistryDelete} = props;
  const store = useStore();

  const regUrls = useSelector(selectRegistryUrls, shallowEqual);
  const giftRegistryType = useSelector((state) => state.giftingOptions?.gift_registry_type);
  const giftRegistryName = useSelector((state) => state.giftingOptions?.gift_registry_name);

  const isWishlist = giftRegistryType === 'wishlist';
  const [registryName, setRegistryName] = useState(giftRegistryName);
  const [registryUrl, setRegistryUrl] = useState('');

  const [registryUrlError, setRegistryUrlError] = useState('');

  const [addDisabled, setAddDisabled] = useState(true);
  const [addReg, setAddReg] = useState(!regUrls.length);

  const confirmDeleteLastRegitry = useSelector(selectConfirmDeleteLastRegistry);

  function scrollToSection() {
    const section = document.querySelector('.submodule-registry');
    if (section) {
      section.scrollIntoView({behavior: 'instant'});
    }
  }

  useEffect(() => {
    if (regUrls.length === 0) setAddReg(true);
  }, [regUrls]);

  const handleAddRegistry = async () => {
    if (!registryUrl) return;
    const formattedRegistryUrl = formatUrlWithProtocol(registryUrl);

    try {
      const url = new URL(formattedRegistryUrl);

      if (regUrls.length < 3) {
        store.dispatch({
          type: UPDATE_GIFT_OPTIONS,
          payload: {
            gift_registry_urls: [...regUrls, url.toString()],
            gift_registry_name: giftRegistryName || registryName,
          },
        });
        setRegistryUrl('');
        setRegistryUrlError('');
        setAddReg(false);
      }
      await store.dispatch(saveEventThunk());
      window.dataLayer.push({
        event: 'registry_note_title_added',
      });
      scrollToSection();
    } catch (_err) {
      setRegistryUrlError('Please enter a valid URL.');
    }
  };

  const formattedRegistryVendors = useMemo(
    () =>
      eviteVendors.map((v) => ({
        ...v,
        domains: v.domains.map((d) => {
          const formattedDomain = formatUrlWithProtocol(d);
          return new URL(formattedDomain).hostname;
        }),
      })),
    []
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    if (nextButtonClicked) {
      handleAddRegistry();
    }
  }, [nextButtonClicked]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    if (registryUrl === '' || !validURL(registryUrl)) {
      setAddDisabled(true);
    } else {
      setAddDisabled(false);
    }
  }, [registryName, registryUrl]);

  return (
    <>
      <div className="create-flow__overlay-form">
        <div className="registry-nudge-area">
          <ThreeQuarterFilledEllipseIcon color={tanzanite} />
          <Typography variant="paragraph4">
            <b className="registry-nudge-text-semibold">Did you know? 65% </b> of guests prefer some
            gift ideas!
          </Typography>
        </div>
        <Vendors type={giftRegistryType} />
        <Input
          className="gift-registry__name"
          data-qa-id="registry-name"
          type="text"
          label="Recipient's name"
          value={registryName}
          onChange={(e) => {
            setRegistryName(e.target.value);
          }}
          placeholder="Recipient's name"
        />
      </div>
      {regUrls.map((registry) => {
        const reg = new URL(registry);
        const vendor = formattedRegistryVendors.find((v) => v.domains.includes(reg.hostname));

        const handleDeleteRegistry = async () => {
          if (confirmDeleteLastRegitry) {
            return onRegistryDelete('registries');
          }

          const url = (path) => path !== registry;
          const giftRegistryURLs = regUrls.filter(url);

          store.dispatch({
            type: UPDATE_GIFT_OPTIONS,
            payload: {gift_registry_urls: giftRegistryURLs},
          });

          if (giftRegistryURLs.length === 0) {
            setRegistryName('');
            store.dispatch({
              type: UPDATE_GIFT_OPTIONS,
              payload: {
                gift_registry_urls: [],
                gift_registry_name: '',
              },
            });
            setAddReg(true);
          }

          return store.dispatch(saveEventThunk());
        };

        return (
          <div className="gift-registry__added-item" key={registry}>
            <HeartFilledIcon />
            <Typography
              as="h4"
              variant="header4"
              data-qa-id="giftRegistryAddedItemTitle"
              color="charcoal"
            >
              View {vendor?.name || capitalize(nth(reg.host.split('.'), -2))}{' '}
              {isWishlist ? 'Wishlist' : 'Registry'}
            </Typography>
            <Button
              data-qa-id="registry-delete"
              className="gift-registry__delete-btn"
              variant="link"
              size="small"
              onClick={handleDeleteRegistry}
              type="button"
            >
              Delete
            </Button>
          </div>
        );
      })}
      {regUrls.length < 3 && (
        <>
          {addReg && (
            <>
              {regUrls.length > 0 && <Divider marginTop="1rem" marginBottom="1rem" />}
              <div className="create-flow__add-input">
                <Input
                  className="gift-registry__url"
                  data-qa-id="registry-url"
                  disabled={regUrls.length >= 3}
                  error={registryUrlError}
                  type="text"
                  value={registryUrl}
                  onChange={(e) => setRegistryUrl(e.target.value)}
                  placeholder={isWishlist ? 'Wishlist URL' : 'Add a gift registry URL'}
                />
                <Button
                  data-qa-id="gift-registry__add-btn"
                  className="gift-registry__add-btn"
                  variant="secondary"
                  size="large"
                  disabled={addDisabled}
                  onClick={handleAddRegistry}
                >
                  Add
                </Button>
              </div>
            </>
          )}
          {regUrls.length > 0 && (
            <Button
              variant="primary"
              size="small"
              className="create-flow__additional-registry"
              data-qa-id="create-flow__additional-registry"
              onClick={() => !!regUrls.length && setAddReg(!addReg)}
              type="button"
            >
              + Additional {isWishlist ? 'Wishlist' : 'Registry'} Link
            </Button>
          )}
        </>
      )}
    </>
  );
};

export const Vendors = ({type}) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      marginBottom: '1rem',
    }}
  >
    <Typography style={{marginRight: 'auto'}} variant="header4">
      Add {type === 'wishlist' ? ' a wishlist' : 'gift registry'}
    </Typography>
    <img src={eviteVendors[0].icon_url} style={{maxHeight: '2rem'}} alt={eviteVendors[0].name} />
    <img src={eviteVendors[1].icon_url} style={{maxHeight: '1.25rem'}} alt={eviteVendors[1].name} />
    <img
      src={eviteVendors[2].icon_url}
      style={{maxHeight: '1.25rem', marginRight: '0.5rem'}}
      alt={eviteVendors[2].name}
    />
    <Typography variant="list2" color="coin">
      +&nbsp;more
    </Typography>
  </div>
);
